import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Market {
  id: string
  name: string
  region: string
  market_size: number
  growth_rate: number
  competition_level: string
  opportunity_score: number
}

interface Trend {
  id: string
  name: string
  category: string
  direction: string
  strength: number
  impact: string
  timeframe: string
}

interface Statistics {
  total_markets: number
  high_opportunity_markets: number
  avg_growth_rate: number
  total_market_size: number
  emerging_markets: number
  saturated_markets: number
  trends_identified: number
  opportunities: number
}

export default function MarketIntelligencePage() {
  const [markets, setMarkets] = useState<Market[]>([])
  const [trends, setTrends] = useState<Trend[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'markets' | 'trends'>('markets')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [analysisRes, trendsRes, statsRes] = await Promise.all([
        fetch('/api/marketintelligence/analysis'),
        fetch('/api/marketintelligence/trends'),
        fetch('/api/marketintelligence/statistics')
      ])

      if (analysisRes.ok) setMarkets(await analysisRes.json())
      if (trendsRes.ok) setTrends(await trendsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching market intelligence data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getCompetitionBadgeClass = (level: string) => {
    const classes: Record<string, string> = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    }
    return classes[level.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  const getTrendDirectionIcon = (direction: string) => {
    if (direction === 'up') return '📈'
    if (direction === 'down') return '📉'
    return '➡️'
  }

  const getImpactBadgeClass = (impact: string) => {
    const classes: Record<string, string> = {
      high: 'bg-purple-100 text-purple-800',
      medium: 'bg-blue-100 text-blue-800',
      low: 'bg-gray-100 text-gray-800'
    }
    return classes[impact.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  const getOpportunityColor = (score: number) => {
    if (score >= 80) return 'bg-green-500'
    if (score >= 60) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Market Intelligence</h1>
          <p className="mt-2 text-gray-600">AI-powered market analysis and expansion opportunities</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Markets</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_markets}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Market Size</p>
              <p className="text-2xl font-bold text-blue-600">${(statistics.total_market_size / 1000000000).toFixed(1)}B</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">High Opportunity</p>
              <p className="text-2xl font-bold text-green-600">{statistics.high_opportunity_markets}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Growth</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_growth_rate.toFixed(1)}%</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('markets')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'markets'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Markets ({markets.length})
            </button>
            <button
              onClick={() => setActiveTab('trends')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'trends'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Trends ({trends.length})
            </button>
          </nav>
        </div>

        {/* Markets Table */}
        {activeTab === 'markets' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Market</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Region</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Growth</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Competition</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opportunity</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {markets.map((market) => (
                  <tr key={market.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900">{market.name}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {market.region}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${(market.market_size / 1000000).toFixed(0)}M
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${market.growth_rate >= 10 ? 'text-green-600' : market.growth_rate >= 5 ? 'text-yellow-600' : 'text-gray-600'}`}>
                        {market.growth_rate >= 0 ? '+' : ''}{market.growth_rate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCompetitionBadgeClass(market.competition_level)}`}>
                        {market.competition_level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className={`h-2 rounded-full ${getOpportunityColor(market.opportunity_score)}`}
                            style={{ width: `${market.opportunity_score}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{market.opportunity_score}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Trends Cards */}
        {activeTab === 'trends' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {trends.map((trend) => (
              <div key={trend.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{getTrendDirectionIcon(trend.direction)}</span>
                    <div>
                      <p className="text-sm text-gray-500">{trend.category}</p>
                      <h3 className="text-lg font-semibold text-gray-900">{trend.name}</h3>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getImpactBadgeClass(trend.impact)}`}>
                    {trend.impact} impact
                  </span>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Strength</span>
                    <span className="font-medium text-gray-900">{trend.strength}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${trend.strength}%` }}
                    ></div>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">Timeframe: {trend.timeframe}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
